import {
  Injectable,
  ViewContainerRef
} from '@angular/core';
import {ReplaySubject} from "rxjs";
import {RequestService} from "../request/request.service";
import {ScheduleConfigurationModel} from "./schedule-configuration.model";
import {ScheduleResourceConfigurationModel} from "./schedule-resource-configuration.model";
import {ScheduleViewConfigurationModel} from "./schedule-view-configuration.model";
import {ScheduleEventConfigurationModel} from "./schedule-event-configuration.model";
import {ScheduleModel} from "./schedule.model";
import {
  EventSettingsModel,
  GroupModel,
  HeaderRowsModel,
  QuickInfoTemplatesModel, ResourcesModel, TimeScaleModel, WorkHoursModel
} from "@syncfusion/ej2-angular-schedule";
import {ExtendedViewsModel} from "./extended-views.model";
import {ExtendedResourcesModel} from "./extended-resources.model";

import {cloneDeep} from 'lodash';
import {TemplateCompilerService} from "../template-compiler/template-compiler.service";
import {TemplateCompilerOptions} from "../template-compiler/template-compiler-options";

@Injectable({
  providedIn: 'root'
})
export class ScheduleDataService {

  private HEADER_ROW_MAPPING = {
    year: 'Year',
    month: 'Month',
    week: 'Week',
    date: 'Date',
    hour: 'Hour'
  };

  private QUICK_INFO_TEMPLATE_TYPE_MAPPING = {
    both: 'Both',
    cell: 'Cell',
    event: 'Event'
  };

  private WEEK_RULE_MAPPING = {
    firstday: 'FirstDay',
    firstfourdayweek: 'FirstFourDayWeek',
    firstfullweek: 'FirstFullWeek'
  };

  private VIEW_OPTION_MAPPING = {
    day: 'Day',
    week: 'Week',
    work_week: 'WorkWeek',
    month: 'Month',
    year: 'Year',
    agenda: 'Agenda',
    month_agenda: 'MonthAgenda',
    timeline_day: 'TimelineDay',
    timeline_week: 'TimelineWeek',
    timeline_work_week: 'TimelineWorkWeek',
    timeline_month: 'TimelineMonth',
    timeline_year: 'TimelineYear'
  };

  private ORIENTATION_MAPPING = {
    vertical: 'Vertical',
    horizontal: 'Horizontal',
  };

  scheduleContentChanged: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(private requestService: RequestService, private templateCompilerService: TemplateCompilerService) {
    (window as any).WebsocketAPI.registerAPIImplementation("scheduleContent", (data: any, responseBody: any) => {
      this.scheduleContentChanged.next(false);
    });
  }

  async loadRecords(scheduleConfiguration: ScheduleConfigurationModel, tableToCurrentQuery: {[tableName: string]: string} = {}, tableToIds: {[tableName: string]: string[]} = {}) {
    const start = new Date().getTime();
    try {
      const promises = scheduleConfiguration.EventConfiguration.map(async (resource) => {
        const query = tableToCurrentQuery[resource.Table?.value] || null;
        const ids = tableToIds[resource.Table?.value] || null;
        return await this.getControllerData('getEvents', { id: resource.id, query: query, ids: ids });
      });

      const recordArrays = await Promise.all(promises);

      return recordArrays.flat(1);
    } catch (e) {
      console.error(e);
    } finally {
      const end = new Date().getTime();
      console.debug(`ScheduleDataService.loadRecords ${scheduleConfiguration.Key.value} took ${end - start}ms`);
    }
  }

  async loadConfiguration(id: string) {
    const start = new Date().getTime();
    try {
      // Should really try to use the REST API for this kind of thing
      const response = await this
        .getControllerData('getConfiguration', {id: id})
        .catch(error => console.error(error));

      if (response == null) return;

      const data = response.data || response; // why does it come back this way instead response.data

      const configuration: ScheduleConfigurationModel = data['configuration'];
      const dataConfigurations: ScheduleResourceConfigurationModel[] = data['dataConfigurations'];
      const viewConfigurations: ScheduleViewConfigurationModel[] = data['viewConfigurations'];
      const eventConfigurations: ScheduleEventConfigurationModel[] = data['eventConfigurations'];
      const eventDateMappings = data['eventDateMappings'];

      configuration.DataConfiguration = dataConfigurations;
      configuration.ViewConfiguration = viewConfigurations;
      configuration.EventConfiguration = eventConfigurations;

      return {scheduleConfiguration: configuration, scheduleId: configuration.id, scheduleKey: configuration.Key.value, eventDateMappings: eventDateMappings};
  } finally {
      const end = new Date().getTime();
      console.debug(`ScheduleDataService.loadConfiguration ${id} took ${end - start}ms`);
    }
}


  async processConfiguration(config: ScheduleConfigurationModel, vcRef?: ViewContainerRef, compRef?: any, templateCompilerOptions?: TemplateCompilerOptions) {

    const title = config.Name.displayValue;
    const subtitle = config.Description.displayValue;

    let schedule: ScheduleModel = {
      mergeGrouping: {
        enabled: config.MergeGrouping.value,
        eventForeignIdFields: [],
        eventParentIds: {}
      }
    };

    // Copy fields across to the schedule
    // numbers and booleans are copied across, strings are copied if defined
    // there is also special handling for dates and string arrays

    // Basic sequence model
    schedule.liveUpdate = config.LiveUpdate.value;
    schedule.agendaDaysCount = config.AgendaDaysCount.value;

    schedule.allowDragAndDrop = config.AllowDragAndDrop.value;
    schedule.allowInline = config.AllowInline.value;
    schedule.allowKeyboardInteraction = config.AllowKeyboardInteraction.value;
    schedule.allowKeyboardInteraction = config.AllowKeyboardInteraction.value;
    schedule.allowKeyboardInteraction = config.AllowKeyboardInteraction.value;
    schedule.allowMultiCellSelection = config.AllowMultiCellSelection.value;
    schedule.allowMultiDrag = config.AllowMultiDrag.value;
    schedule.allowMultiRowSelection = config.AllowMultiRowSelection.value;
    schedule.allowResizing = config.AllowResizing.value;
    schedule.hideEmptyAgendaDays = config.HideEmptyAgendaDays.value;
    schedule.enableAllDayScroll = config.EnableAllDayScroll.value;
    schedule.readOnly = config.ReadOnly.value;
    schedule.rowAutoHeight = config.RowAutoHeight.value;
    schedule.showHeaderBar = config.ShowHeaderBar.value;
    schedule.showQuickInfo = config.ShowQuickInfo.value;
    schedule.showTimeIndicator = config.ShowTimeIndicator.value;
    schedule.showWeekNumber = config.ShowWeekNumber.value;
    schedule.showWeekend = config.ShowWeekend.value;

    this.copyDefinedNumberValue(config, schedule, "FirstDayOfWeek", "firstDayOfWeek", 1);
    this.copyDefinedValueNG(config, schedule, "CellHeaderTemplate", "cellHeaderTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValueNG(config, schedule, "CellTemplate", "cellTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValue(config, schedule, "DateFormat", "dateFormat");
    this.copyDefinedValueNG(config, schedule, "DateHeaderTemplate", "dateHeaderTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValueNG(config, schedule, "EditorTemplate", "editorTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValue(config, schedule, "EndHour", "endHour");
    this.copyDefinedDateValue(config, schedule, "SelectedDate", "selectedDate");
    this.copyDefinedValue(config, schedule, "StartHour", "startHour");
    this.copyDefinedValue(config, schedule, "Timezone", "timezone");

    this.copyDefinedValue(config, schedule, "CurrentView", "currentView");

    this.copyDefinedDateValue(config, schedule, "MinDate", "minDate");
    this.copyDefinedDateValue(config, schedule, "MaxDate", "maxDate");
    this.copyDefinedValueNG(config, schedule, "ResourceHeaderTemplate", "resourceHeaderTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedMappedValue(config, schedule, "WeekRule", "weekRule", this.WEEK_RULE_MAPPING);
    this.copyDefinedCSVNumberValue(config, schedule, "WorkDays", "workDays");

    // Event settings
    schedule.eventSettings = {} as EventSettingsModel;
    this.copyDefinedValueNG(config, schedule.eventSettings, "EventTemplate", "template", vcRef, compRef, templateCompilerOptions);
    schedule.eventSettings.enableTooltip = config.EnableTooltip.value;
    this.copyDefinedValueNG(config, schedule.eventSettings, "TooltipTemplate", "tooltipTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValue(config, schedule.eventSettings, "ResourceColorField", "resourceColorField");
    schedule.eventSettings.editFollowingEvents = config.EditFollowingEvents.value;
    schedule.eventSettings.allowAdding = config.AllowAdding.value;
    schedule.eventSettings.allowEditing = config.AllowEditing.value;
    schedule.eventSettings.allowDeleting = config.AllowDeleting.value;
    schedule.eventSettings.enableMaxHeight = config.EnableMaxHeight.value;
    schedule.eventSettings.enableIndicator = config.EnableIndicator.value;
    schedule.eventSettings.ignoreWhitespace = config.IgnoreWhitespace.value;

    // Group
    schedule.group = {} as GroupModel;
    schedule.group.byDate = config.GroupByDate.value;
    schedule.group.byGroupID = config.GroupByGroupId.value;
    schedule.group.allowGroupEdit = config.GroupAllowGroupEdit.value;
    this.copyDefinedValue(config, schedule.group, "GroupResources", "resources");
    this.copyDefinedValueNG(config, schedule.group, "GroupHeaderTooltipTemplate", "headerTooltipTemplate", vcRef, compRef, templateCompilerOptions);

    // Header rows
    const headerRows: HeaderRowsModel[] = [];
    ['Year', 'Month', 'Week', 'Date', 'Hour'].forEach((h) => {
      if (config[`HeaderRow${h}`]) {
        const headerRow = { option: h } as HeaderRowsModel;
        this.copyDefinedValueNG(config, headerRow, `HeaderRow${h}Template`, 'template', vcRef, compRef, templateCompilerOptions);
      }
    });
    if (headerRows.length > 0) {
      schedule.headerRows = headerRows;
    }

    // Quick info template
    schedule.quickInfoTemplates = {} as QuickInfoTemplatesModel;
    this.copyDefinedMappedValue(config, schedule.quickInfoTemplates, "QuickInfoTemplateType", "templateType", this.QUICK_INFO_TEMPLATE_TYPE_MAPPING);
    this.copyDefinedValueNG(config, schedule.quickInfoTemplates, "QuickInfoHeaderTemplate", "header", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValueNG(config, schedule.quickInfoTemplates, "QuickInfoContentTemplate", "content", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValueNG(config, schedule.quickInfoTemplates, "QuickInfoFooterTemplate", "footer", vcRef, compRef, templateCompilerOptions);

    // Timescale
    schedule.timeScale = {} as TimeScaleModel;
    schedule.timeScale.enable = config.Timescale.value;
    schedule.timeScale.interval = config.TimescaleInterval.value;
    schedule.timeScale.slotCount = config.TimescaleSlotCount.value;
    this.copyDefinedValueNG(config, schedule.timeScale, "TimescaleMinorSlotTemplate", "minorSlotTemplate", vcRef, compRef, templateCompilerOptions);
    this.copyDefinedValueNG(config, schedule.timeScale, "TimescaleMajorSlotTemplate", "majorSlotTemplate", vcRef, compRef, templateCompilerOptions);

    // Work hours
    schedule.workHours = {} as WorkHoursModel;
    schedule.workHours.highlight = config.WorkHoursHighlight.value;
    this.copyDefinedValue(config, schedule.workHours, "WorkHoursStart", "start");
    this.copyDefinedValue(config, schedule.workHours, "WorkHoursEnd", "end");

    // Views
    schedule.views = config.ViewConfiguration.map((v) => {
      const view = {} as ExtendedViewsModel;
      view.id = v.id;

      this.copyDefinedMappedValue(v, view, "Type", "option", this.VIEW_OPTION_MAPPING);
      this.copyDefinedValue(v, view, "DateFormat", "dateFormat");
      view.readonly = v.ReadOnly.value;
      this.copyDefinedValue(v, view, "StartHour", "startHour");
      this.copyDefinedValue(v, view, "EndHour", "endHour");
      this.copyDefinedValueNG(v, view, "CellHeaderTemplate", "cellHeaderTemplate", vcRef, compRef, templateCompilerOptions);
      this.copyDefinedValueNG(v, view, "CellTemplate", "cellTemplate", vcRef, compRef, templateCompilerOptions);
      this.copyDefinedValueNG(v, view, "EventTemplate", "eventTemplate", vcRef, compRef, templateCompilerOptions);
      view.showWeekend = v.ShowWeekend.value;
      view.showWeekNumber = v.ShowWeekNumber.value;
      this.copyDefinedValue(v, view, "DisplayName", "displayName");
      view.interval = v.TypeCount.value;
      this.copyDefinedNumberValue(v, view, "FirstDayOfWeek", "firstDayOfWeek", 1);
      this.copyDefinedCSVNumberValue(v, view, "WorkDays", "workDays");
      this.copyDefinedValueNG(v, view, "ResourceHeaderTemplate", "resourceHeaderTemplate", vcRef, compRef, templateCompilerOptions);
      this.copyDefinedMappedValue(v, view, "Orientation", "orientation", this.ORIENTATION_MAPPING);

      // Timescale
      view.timeScale = {} as TimeScaleModel;
      view.timeScale.enable = v.Timescale.value;
      view.timeScale.interval = v.TimescaleInterval.value;
      view.timeScale.slotCount = v.TimescaleSlotCount.value;
      this.copyDefinedValueNG(v, view.timeScale, "TimescaleMinorSlotTemplate", "minorSlotTemplate", vcRef, compRef, templateCompilerOptions);
      this.copyDefinedValueNG(v, view.timeScale, "TimescaleMajorSlotTemplate", "majorSlotTemplate", vcRef, compRef, templateCompilerOptions);

      // Group - don't include group for a view if it doesn't specify resources otherwise it overrides the one at the schedule level
      if (v.GroupResources && v.GroupResources.value && v.GroupResources.value.length > 0) {
        view.group = {} as GroupModel;
        view.group.byDate = v.GroupByDate.value;
        view.group.byGroupID = v.GroupByGroupId.value;
        view.group.allowGroupEdit = v.GroupAllowGroupEdit.value;
        this.copyDefinedValue(v, view.group, "GroupResources", "resources");
        this.copyDefinedValueNG(v, view.group, "GroupHeaderTooltipTemplate", "headerTooltipTemplate", vcRef, compRef, templateCompilerOptions);
      }

      // Header rows
      const headerRows: HeaderRowsModel[] = [];
      ['Year', 'Month', 'Week', 'Date', 'Hour'].forEach((h) => {
        if (v[`HeaderRow${h}`]) {
          const headerRow = { option: h } as HeaderRowsModel;
          this.copyDefinedValueNG(v, headerRow, `HeaderRow${h}Template`, 'template', vcRef, compRef, templateCompilerOptions);
        }
      });
      if (headerRows.length > 0) {
        view.headerRows = headerRows;
      }

      return view;
    });
    // Views need a default
    if (schedule.views.length === 0) {
      schedule.views = ['Week'];
    }

    // Resources
    schedule.resources = config.DataConfiguration.map((d) => {
      const res = {} as ExtendedResourcesModel;
      res.id = d.id;
      res.serverQuery = d.Query.value;
      res.tableClass = d.Table.value;
      res.dataSource = [];

      this.copyDefinedValue(d, res, "ResourceEventField", "field");
      this.copyDefinedValue(d, res, "ResourceTitle", "title");
      this.copyDefinedValue(d, res, "ResourceGroupingName", "name");
      res.allowMultiple = d.ResourceAllowMultiple.value;

      return res;
    });

    // List node template for the list on the right
    this.copyDefinedValueNG(config, schedule, "ListNodeTemplate", "nodeTemplate", vcRef, compRef, templateCompilerOptions);

    return {title: title, subtitle: subtitle, schedule: schedule};
  }

  // This takes the schedule model and evaluates the resources.
  async processScheduleModel(scheduleModel: ScheduleModel, tableToCurrentQuery: {[tableName: string]: string} = {}, tableToIds: {[tableName: string]: string[]} = {}) {

    const promises = scheduleModel.resources.map(async (resource) => {
      const query = tableToCurrentQuery[resource.tableClass] || null;
      const ids = tableToIds[resource.tableClass] || null;
      resource.dataSource = await this.getControllerData('getResources', { id: resource.id, query: query, ids: ids });
    });

    await Promise.all(promises);

    // If the resources are multi level then they need to be in the list in the same order as the resources list in the
    // grouping model, as the syncfusion code is very sensitive to this.
    const resources: ExtendedResourcesModel[] = scheduleModel.resources;
    const groupedResources: ExtendedResourcesModel[] = [];
    if (scheduleModel.group && scheduleModel.group.resources?.length > 0) {
      scheduleModel.group.resources.forEach((name) => {
        const resource = resources.find((r) => {
          return r.name === name;
        });
        if (resource) {
          groupedResources.push(resource);
        }
      });
    }
    const ungroupedResources = resources.filter((r) => !groupedResources.includes(r));
    scheduleModel.resources = [...groupedResources, ...ungroupedResources];
    scheduleModel = this.performMergeGrouping(scheduleModel);

    return scheduleModel;
  }

  // New implementation of merge grouping. We modify resources definitions in-place to make a complete tree.
  private performMergeGrouping(scheduleModel: ScheduleModel): ScheduleModel {
    if (scheduleModel?.mergeGrouping?.enabled) {
      const group = scheduleModel.group;
      const resources = scheduleModel.resources;
      if (group && resources) {

        const allResourceNames = resources.map(r => r.name);
        const groupedResourceNames = group.resources;
        const nonGroupedResourceNames = allResourceNames.filter(r => !groupedResourceNames.includes(r));

        // We only do merging if there are multiple levels and some other resources not in the current group list.
        if (group.byGroupID && group.resources.length > 1 && nonGroupedResourceNames.length > 0) {

          // For the non-grouped resources identify which grouped resource they need to be part of. We do this by looking at the
          // ResourceEventField of each non-grouped resource and finding a match in the grouped resources. If there is no match then
          // we don't merge them in. These represent resources that are still valid for events in other views but do not appear
          // in the grouping tree for timeline views.
          const groupedResourcesByName = Object.fromEntries(groupedResourceNames.map(n => [n, resources.find(r => r.name == n)]));

          // Collect all the group resources
          const groupedResourcesByField = Object.fromEntries(groupedResourceNames.map(n => {
            const r = resources.find(r => r.name == n);
            return [r.field, r];
          }));

          // Collect all the non-grouped resources that have 'field' defined, i.e. ResourceEventField
          const nonGroupedResources = nonGroupedResourceNames
            .map(n => resources.find(r => r.name == n))
            .filter(r => !!r);

          nonGroupedResources.forEach((r: any) => {
            // The grouped resource that should receive the records from this resource
            const associatedGroupedResource = groupedResourcesByField[r.field];
            // Copy them across and clear the original array.
            if (associatedGroupedResource) {
              (associatedGroupedResource.dataSource as any[]).push(...r.dataSource);
              r.dataSource.length = 0;
            }
          });
        }
      }
    }

    return scheduleModel;
  }

  // Old implementation of merge grouping - too hard to conceptualise and explain.
  //
  // We only do merging if there are two levels and some other resources not in the current group list.
  //
  // We do the following
  // - Copy all fields of merged resources to the merged field names
  // - Make a new resource record for the merge resources
  // - remove the old resource records
  // - Make new top level records for the non-grouped resources
  // - modify all events that reference the foreign ids of merged resources
  // - Add the id of the relevant new top level record to each record from the original resource
  private performMergeGroupingOld(scheduleModel: ScheduleModel): ScheduleModel {

    const mergedFieldMapping = {
      idField: '_mergedId',
      textField: '_mergedText',
      expandedField: '_mergedExpanded',
      groupIDField: '_mergedGroupID',
      colorField: '_mergedColor',
      startHourField: '_mergedStartHourField',
      endHourField: '_mergedEndHourField',
      workDaysField: '_mergedWorkDaysField',
      cssClassField: '_mergedCssClassField',
    };

    const defaultFieldMapping = {
      idField: 'Id',
      textField: 'Text',
      expandedField: 'Expanded',
      groupIDField: 'GroupID',
      colorField: 'Color',
      startHourField: 'StartHour',
      endHourField: 'EndHour',
      workDaysField: 'WorkDays',
      cssClassField: 'CssClass',
    };

    if (scheduleModel?.mergeGrouping?.enabled) {
      const group = scheduleModel.group;
      const resources = scheduleModel.resources;
      if (group && resources) {

        const allResourceNames = resources.map(r => r.name);
        const groupedResourceNames = group.resources;
        const nonGroupedResourceNames = allResourceNames.filter(r => !groupedResourceNames.includes(r));

        // We only do merging if there are two levels and some other resources not in the current group list.
        if (group.byGroupID && group.resources.length > 1 && nonGroupedResourceNames.length > 0) {
          // We do the following
          // - Copy all fields of merged resources to the merged field names
          // - Make a new resource record for the merge resources
          // - remove the old resource records
          // - Make new top level records for the non-grouped resources
          // - modify all events that reference the foreign ids of merged resources
          // - Add the id of the relevant new top level record to each record from the original resource

          const getParameterCaseInsensitive = (object, key) => {
            return !key ? null : object[Object.keys(object).find(k => k.toLowerCase() === key.toLowerCase())];
          };

          const getResourceByName = (resources: any[], name: string) => {
            return resources.find(r => r.name === name);
          }

          const topLevelResource = getResourceByName(resources, group.resources[0]);
          const mergeTargetResource = getResourceByName(resources, group.resources[1]);

          const newResourceName = [mergeTargetResource.name, ...nonGroupedResourceNames].join('-');
          const mergedRecords = [];

          [mergeTargetResource.name, ...nonGroupedResourceNames].forEach((r) => {
            const oldResource = resources.find(rs => rs.name === r);
            const oldRecords = oldResource.dataSource as any[];
            const newRecords = oldRecords.map(oldRecord => {
              const newRecord = cloneDeep(oldRecord);

              Object.keys(defaultFieldMapping).forEach((propKey) => {
                const propName = defaultFieldMapping[propKey];
                if (propName) {
                  const propValue = getParameterCaseInsensitive(oldRecord, propName);
                  if (propValue) {
                    newRecord[mergedFieldMapping[propKey]] = propValue;
                  }
                }
              });

              Object.keys(mergedFieldMapping).forEach((propKey) => {
                const propName = oldResource[propKey];
                if (propName) {
                  const propValue = getParameterCaseInsensitive(oldRecord, propName);
                  if (propValue) {
                    newRecord[mergedFieldMapping[propKey]] = propValue;
                  }
                }
              });

              // Some of the old resources dont have a group id field so we add one
              if (!newRecord[mergedFieldMapping.groupIDField]) {
                newRecord[mergedFieldMapping.groupIDField] = oldResource.name;
              }

              return newRecord;
            });
            mergedRecords.push(...newRecords);
          });

          // Build new group to replace the old group
          const newGroup: GroupModel = {
            byDate: group.byDate,
            byGroupID: group.byGroupID,
            allowGroupEdit: group.allowGroupEdit,
            resources: [topLevelResource.name, newResourceName],
            enableCompactView: group.enableCompactView,
            headerTooltipTemplate: group.headerTooltipTemplate
          };

          // Build new resource to replace all the non-grouped resources
          const newResource: ResourcesModel = {
            field: '_mergedField',
            title: newResourceName,
            name: newResourceName,
            allowMultiple: true,
            dataSource: mergedRecords,
            query: null,
            idField: '_mergedId',
            textField: '_mergedText',
            expandedField: '_mergedExpanded',
            groupIDField: '_mergedGroupID',
            colorField: '_mergedColor',
            startHourField: '_mergedStartHours',
            endHourField: '_mergedEndHourField',
            workDaysField: '_mergedWorkDays',
            cssClassField: '_mergedCssClass'
          };

          // Collect all the old resources while removing them from the resources list
          const nonGroupedResources = [];
          nonGroupedResourceNames.forEach((resName) => {
            nonGroupedResources.push(...resources.splice(resources.findIndex(r => r.name === resName), 1));
          });

          // Remove the merge target from the resource list
          resources.splice(resources.findIndex(r => r.name === mergeTargetResource.name), 1);

          // Add the new merged resource to the resources list
          resources.push(newResource);

          scheduleModel.resources = resources;
          scheduleModel.group = newGroup;

          // Make new top level records

          const topLevelFieldField = topLevelResource['field'];
          const topLevelIdField = topLevelResource['idField'] || 'Id';
          const topLevelTextField = topLevelResource['textField'] || 'Text';

          const newRecords = nonGroupedResourceNames.map(recName => {
            const newRecord: any = {};
            newRecord[topLevelIdField] = recName;
            newRecord[topLevelTextField] = recName;

            return newRecord;
          });

          (topLevelResource.dataSource as any[]).unshift(...newRecords);

          const mergeTargetFieldField = mergeTargetResource['field'];
          const mergeTargetIdField = mergeTargetResource['idField'] || 'Id';
          const mergeTargetTextField = mergeTargetResource['textField'] || 'Text';

          // Fields that need to be copied to '_mergedField', which must be an array.
          const eventForeignIdFields = nonGroupedResources.map(r => {
            return getParameterCaseInsensitive(r, "field");
          });

          // Add the merge target field as well
          eventForeignIdFields.push(mergeTargetResource['field']);

          // Extra fields that need to be added to all event records that reference a non-grouped resource
          const eventParentIds = {};

          nonGroupedResources.forEach((r) => {
            eventParentIds[getParameterCaseInsensitive(r, "field")] = {
              id: topLevelFieldField,
              value: r.name
            };
          });

          scheduleModel.mergeGrouping = {
            enabled: true,
            eventForeignIdFields: eventForeignIdFields,
            eventParentIds: eventParentIds
          }
        }
      }
    }

    return scheduleModel;
  }

  private isDefined(value: any) {
    return value !== null && value !== undefined;
  }

  private copyDefinedValue(from: any, to: any, fromField: string, toField: string, defaultValue?: any) {
    if (this.isDefined(from[fromField].value)) {
      to[toField] = from[fromField].value;
    } else if (defaultValue) {
      to[toField] = defaultValue;
    }
  }

  private copyDefinedNumberValue(from: any, to: any, fromField: string, toField: string, defaultValue?: number) {
    if (this.isDefined(from[fromField].value)) {
      to[toField] = Number(from[fromField].value);
    } else if (defaultValue !== undefined) {
      to[toField] = defaultValue;
    }
  }

  private copyDefinedMappedValue(from: any, to: any, fromField: string, toField: string, mapping: any) {
    if (this.isDefined(from[fromField].value)) {
      if (mapping) {
        to[toField] = mapping[from[fromField].value];
      } else {
        to[toField] = from[fromField].value;
      }
    }
  }

  private copyDefinedDateValue(from: any, to: any, fromField: string, toField: string) {
    if (this.isDefined(from[fromField].value)) {
      to[toField] = new Date(from[fromField].value);
    }
  }

  // Value could be "1,2" or ["1","2"] or [1,2]
  private copyDefinedCSVNumberValue(from: any, to: any, fromField: string, toField: string) {
    const value = from[fromField].value;
    if (this.isDefined(value)) {
      to[toField] = (typeof(value) === "string" ? value.replace(/ /g, '').split(',') : value).map((n) => Number(n));
    }
  }

  private async copyDefinedValueNG(from: any, to: any, fromField: string, toField: string, vcRef: ViewContainerRef, compRef: any, templateCompilerOptions: TemplateCompilerOptions) {
    if (this.isDefined(from[fromField].value)) {
      to[toField] = await this.templateCompilerService.compileTemplate(from[fromField].value, toField, vcRef, compRef, templateCompilerOptions);
    }
  }

  private async getControllerData(method: string, data: any = {}) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Schedule")
      .method(method)
      .data(data)
      .execute();

    return response.data;
  }

}
